import { Injectable, NgZone } from '@angular/core';
import { FullScreenHandlerService } from '../../shared/services/full-screen-handler/full-screen-handler.service';
import { throttle } from 'lodash';
import { NotificationBarService } from '../../shared/services/notification/notification-bar.service';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Injectable()
export class ActivityHandlerService {
  private static readonly INACTIVITY_NOTIFICATION_TIMEOUT: number = 30000;
  static readonly INACTIVITY_LOGOUT_REASON: string = 'inactivity-logout-reason';
  static readonly INACTIVITY_LOGOUT_WARNING: string = 'inactivity-logout-warning';
  private readonly _throttledResetTimer: Function;
  private readonly _resetTimeoutHandler;
  private readonly _iFrameMouseOverEventHandler;
  private readonly _iFrameMouseOutEventHandler;
  private readonly _fullScreenChangeHandler;
  private _timeoutCacheInMilliSeconds: number;
  private _activityTimer;
  private _inActivityNotificationTimer;
  private _iFrameCheckInterval;
  private _cachedIFrames = [];
  private _isTimerStopped: boolean = false;

  constructor(private commonAuthService: CommonAuthService,
              private ngZone: NgZone,
              private bcNotificationService: NotificationBarService) {
    this._throttledResetTimer = throttle(() => {
      if (!this._isTimerStopped) {
        this._clearActiveTimers();
        this._setTimer();
      }
    }, 1000, { leading: true, trailing: false });

    this._resetTimeoutHandler = () => {
      this._resetTimer();
    };
    this._iFrameMouseOverEventHandler = () => {
      if (!FullScreenHandlerService.isFullscreen()) {
        this._stopTimer();
      }
    };
    this._iFrameMouseOutEventHandler = () => {
      if (!FullScreenHandlerService.isFullscreen()) {
        this._startTimer();
      }
    };
    this._fullScreenChangeHandler = () => {
      if (FullScreenHandlerService.isFullscreen()) {
        this._stopTimer();
      } else {
        this._startTimer();
      }
    };
  }

  public get resetTimeoutHandler() {
    return this._resetTimeoutHandler;
  }

  public get isActivityTimerActive(): boolean {
    return !!this._activityTimer;
  }

  public get isIFrameCheckerActive(): boolean {
    return !!this._iFrameCheckInterval;
  }

  public get isTimerStopped(): boolean {
    return this._isTimerStopped;
  }

  public initialize(autoLogoutTimeoutInMinutes: number): void {
    this.bcNotificationService.removeNotification(ActivityHandlerService.INACTIVITY_LOGOUT_REASON);
    this.cleanUp();

    this._timeoutCacheInMilliSeconds = autoLogoutTimeoutInMinutes * 60 * 1000;
    this._registerListenersAndSetTimer();
    this._checkIFrames();
  }

  public cleanUp(): void {
    this._clearListenersAndTimer();
    this._clearIFrameChecker();
    FullScreenHandlerService.removeOnChangeHandler(this._fullScreenChangeHandler);
  }

  private _registerListenersAndSetTimer(): void {
    if (!this._isTimerStopped) {
      this._registerEventListenersToDocument();
      this._setTimer();
    }
  }

  private _clearListenersAndTimer(): void {
    this._clearActiveTimers();
    this._clearEventListenersFromDocument();
  }

  private _resetTimer(): void {
    this._throttledResetTimer();
  }

  private _setTimer(): void {
    this._activityTimer = setTimeout(async () => {
      if (!this._isTimerStopped) {
        this.cleanUp();
        await this.commonAuthService.logout();
        this.bcNotificationService.addNotification({ id: ActivityHandlerService.INACTIVITY_LOGOUT_REASON, content: 'activityHandler.inactivityLogoutReason' });
      }
    }, this._timeoutCacheInMilliSeconds);

    const inactivityNotificationTimeout: number = this._timeoutCacheInMilliSeconds - ActivityHandlerService.INACTIVITY_NOTIFICATION_TIMEOUT;
    this._inActivityNotificationTimer = setTimeout(() => {
      if (!this._isTimerStopped) {
        this.bcNotificationService.addNotification({ id: ActivityHandlerService.INACTIVITY_LOGOUT_WARNING, content: 'activityHandler.inactivityLogoutWarning' });
      }
    }, inactivityNotificationTimeout);
  }

  private _clearActiveTimers(): void {
    if (this._activityTimer) {
      clearTimeout(this._activityTimer);
      this._activityTimer = null;
    }

    if (this._inActivityNotificationTimer) {
      clearTimeout(this._inActivityNotificationTimer);
      this._inActivityNotificationTimer = null;
    }

    this.bcNotificationService.removeNotification(ActivityHandlerService.INACTIVITY_LOGOUT_WARNING);
  }

  private _clearIFrameChecker(): void {
    if (this._iFrameCheckInterval) {
      clearInterval(this._iFrameCheckInterval);
      this._iFrameCheckInterval = null;
    }
  }

  private _registerEventListenersToDocument(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('keypress', this._resetTimeoutHandler, false);
      document.addEventListener('mousemove', this._resetTimeoutHandler, false);
      document.addEventListener('mousedown', this._resetTimeoutHandler, false);
      FullScreenHandlerService.addOnChangeHandler(this._fullScreenChangeHandler);
    });
  }

  private _clearEventListenersFromDocument(): void {
    document.removeEventListener('keypress', this._resetTimeoutHandler);
    document.removeEventListener('mousemove', this._resetTimeoutHandler);
    document.removeEventListener('mousedown', this._resetTimeoutHandler);
  }

  private _checkIFrames(): void {
    this._iFrameCheckInterval = setInterval(() => {
      const elements = document.getElementsByTagName('iframe');
      const elementsArray = Array.from(elements);

      this._cachedIFrames.filter(element => !elementsArray.includes(element)).forEach(element => {
        this._clearEventListenersForIFrame(element);
        const indexOfElementToRemove = this._cachedIFrames.indexOf(element);
        this._cachedIFrames.splice(indexOfElementToRemove, 1);
      });

      elementsArray.forEach(element => {
        if (!this._cachedIFrames.includes(element)) {
          this._registerEventListenersForIFrame(element);
          this._cachedIFrames.push(element);
        }
      });
    }, 1000);
  }

  private _registerEventListenersForIFrame(element: HTMLIFrameElement): void {
    element.addEventListener('mouseover', this._iFrameMouseOverEventHandler, false);
    element.addEventListener('mouseout', this._iFrameMouseOutEventHandler, false);
  }

  private _clearEventListenersForIFrame(element: HTMLIFrameElement): void {
    element.removeEventListener('mouseover', this._iFrameMouseOverEventHandler);
    element.removeEventListener('mouseout', this._iFrameMouseOutEventHandler);
  }

  private _stopTimer(): void {
    this._isTimerStopped = true;
  }

  private _startTimer(): void {
    this._isTimerStopped = false;
    this._clearActiveTimers();
  }
}
